// Pestaña Cuenta PCF: la tarjeta PagoConFiado del cliente, la CLABE con la que recibe fondos y el
// bono de bienvenida. Los tres bloques llegan del resumen 360.
import { useState } from 'react';
import { ErrorState } from '@/kit';
import { cn } from '@/lib/cn';
import { customerFullName } from '@/api/services/retail-customer/retail-customer.client';
import { formatDateMx } from '@/lib/dateHelpers';
import { summaryErrorCopy } from '../lib/creditCustomersErrors';
import { PCF_INSTITUTION_NAME } from '../lib/customer360';
import { walletReadOf, type WalletRead } from '../lib/customerWallet';
import { cardReadOf, type CardRead } from '../lib/customerCard';
import { welcomeBonusReadOf, type WelcomeBonusRead } from '../lib/customerWelcomeBonus';
import { formatMoneyCents } from '../lib/creditCustomersFormat';
import type { CustomerSummaryState } from '../api/useCustomer360';
import { CHIP, Field, FileCard } from './CustomerFileUi';

/** Lo que se pinta en la celda que el silo dejó vacía. */
const EMPTY_VALUE = '—';

const CHIP_SUCCESS = 'border-success bg-success/10 text-success';

const CHIP_MUTED = 'border-muted-foreground text-muted-foreground';

export function CustomerPcfTab({ summary }: { summary: CustomerSummaryState }) {
    if (summary.failure) {
        const copy = summaryErrorCopy(summary.failure);
        return (
            <ErrorState
                title={copy.title}
                description={copy.description}
                onRetry={copy.canRetry ? summary.retry : undefined}
            />
        );
    }

    const customer = summary.data?.customer;
    const isLoading = summary.isLoading;
    const wallet = walletReadOf(summary.data?.wallet);
    const card = cardReadOf(summary.data?.card);
    const bonus = welcomeBonusReadOf(summary.data?.welcomeBonus);

    return (
        <>
            <FileCard title="Tarjeta física PagoConFiado">
                <Field label="Estado">
                    {isLoading ? <PendingValue /> : <CardStateChip card={card} />}
                </Field>
                <Field label="Últimos 4">
                    {isLoading ? <PendingValue /> : <CardDigitsValue card={card} />}
                </Field>
                <Field label="Emitida el">
                    {isLoading ? <PendingValue /> : <CardIssuedAtValue card={card} />}
                </Field>
            </FileCard>

            <FileCard title="CLABE virtual (recepción de fondos)">
                <Field label="CLABE">
                    {isLoading ? <PendingValue /> : <ClabeValue wallet={wallet} />}
                </Field>
                <Field label="Institución" wrap>
                    {PCF_INSTITUTION_NAME}
                </Field>
                <Field label="Titular">
                    {isLoading || !customer ? <PendingValue /> : customerFullName(customer)}
                </Field>
            </FileCard>

            <FileCard title="Bienvenida">
                <Field label="Bono de bienvenida">
                    {isLoading ? <PendingValue /> : <WelcomeBonusChip bonus={bonus} />}
                </Field>
            </FileCard>
        </>
    );
}

/** El dato viene en camino: un guion afirmaría una ausencia que todavía no se sabe. */
function PendingValue() {
    return <span className="text-muted-foreground">…</span>;
}

/** La tarjeta está emitida o está por emitirse. No hay tercer estado que pintar. */
function CardStateChip({ card }: { card: CardRead }) {
    const isIssued = card.state === 'issued';
    return (
        <span className={cn(CHIP, isIssued ? CHIP_SUCCESS : CHIP_MUTED)}>
            {isIssued ? 'Emitida' : 'Pendiente'}
        </span>
    );
}

function CardDigitsValue({ card }: { card: CardRead }) {
    if (card.state !== 'issued' || card.panLastDigit === null) return <>{EMPTY_VALUE}</>;
    return <span className="font-mono tabular-nums">{`••${card.panLastDigit}`}</span>;
}

function CardIssuedAtValue({ card }: { card: CardRead }) {
    if (card.state !== 'issued' || card.issuedAt === null) return <>{EMPTY_VALUE}</>;
    return <>{formatDateMx(card.issuedAt)}</>;
}

/** La CLABE completa, tal cual la manda el silo, con el botón que la copia. */
function ClabeValue({ wallet }: { wallet: WalletRead }) {
    if (wallet.state !== 'read' || wallet.clabe === null) return <>{EMPTY_VALUE}</>;

    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="font-mono tabular-nums">{wallet.clabe}</span>
            <CopyButton value={wallet.clabe} />
        </span>
    );
}

function CopyButton({ value }: { value: string }) {
    const [isCopied, setIsCopied] = useState(false);

    return (
        <button
            type="button"
            onClick={() => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- el tipo DOM marca clipboard como siempre presente, pero falta en contextos no seguros / jsdom
                if (!navigator.clipboard) return;
                void navigator.clipboard.writeText(value).then(() => {
                    setIsCopied(true);
                });
            }}
            className="rounded border border-border px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted hover:text-foreground"
        >
            {isCopied ? 'Copiado' : 'Copiar'}
        </button>
    );
}

/** El bono se aplicó o no se aplicó: solo el asiento acreditado dice que el cliente cobró. */
function WelcomeBonusChip({ bonus }: { bonus: WelcomeBonusRead }) {
    const entry = bonus.state === 'entry' ? bonus.entry : null;
    if (entry === null || entry.status !== 'ACCREDITED') {
        return <span className={cn(CHIP, CHIP_MUTED)}>Sin bono</span>;
    }

    const amount = entry.amountCents === null ? '' : ` · ${formatMoneyCents(entry.amountCents)}`;
    return <span className={cn(CHIP, CHIP_SUCCESS)}>{`Aplicado${amount}`}</span>;
}
